// The label of every icon-only button, shown in the shared dark tip (.ds-tip)
// when the pointer rests on it or the keyboard lands on it. One listener on
// the document covers React islands and server-drawn pages alike, so a new
// icon button gets its tip from its aria-label with no extra code.
//
// What gets a tip:
//  - anything with data-tip="text" (an empty data-tip turns it off);
//  - a button, link or role="button" with an aria-label and no visible text.
// A native title is left to the browser, and ui/Tooltip's own triggers
// (data-tip-owner) draw their own card.

const SELECTOR = '[data-tip], button[aria-label], a[aria-label], [role="button"][aria-label]'
const HOVER_DELAY = 400
const GAP = 6

let tip: HTMLDivElement | null = null
let shownFor: HTMLElement | null = null
let timer: number | undefined

export function tipText(el: Element): string | null {
  if (el.closest('[data-tip-owner]')) return null
  if (el.hasAttribute('data-tip')) return el.getAttribute('data-tip') || null
  if (el.hasAttribute('title')) return null
  if (el.getAttribute('aria-expanded') === 'true') return null
  if ((el.textContent ?? '').trim()) return null
  return el.getAttribute('aria-label') || null
}

export function tipTarget(node: EventTarget | null): HTMLElement | null {
  if (!(node instanceof Element)) return null
  const el = node.closest<HTMLElement>(SELECTOR)
  return el && tipText(el) ? el : null
}

function place(el: HTMLElement, box: HTMLDivElement) {
  const r = el.getBoundingClientRect()
  const w = box.offsetWidth
  const h = box.offsetHeight
  let y = r.top - h - GAP
  if (y < 8) y = r.bottom + GAP
  const x = Math.max(8, Math.min(r.left + r.width / 2 - w / 2, window.innerWidth - w - 8))
  box.style.left = `${Math.round(x)}px`
  box.style.top = `${Math.round(Math.min(y, window.innerHeight - h - 8))}px`
}

export function showTip(el: HTMLElement) {
  const text = tipText(el)
  if (!text) return
  if (!tip) {
    tip = document.createElement('div')
    tip.className = 'ds-tip'
    tip.setAttribute('role', 'tooltip')
    tip.id = 'ds-icon-tip'
  }
  if (!tip.isConnected) document.body.appendChild(tip)
  tip.textContent = text
  tip.style.opacity = '0'
  tip.hidden = false
  shownFor = el
  place(el, tip)
  tip.style.opacity = '1'
}

export function hideTip() {
  window.clearTimeout(timer)
  timer = undefined
  shownFor = null
  if (tip) tip.hidden = true
}

/** Wire the tips once per page. Safe to call again. */
export function installIconTips(doc: Document = document) {
  const w = doc.defaultView as (Window & { __dsIconTips?: boolean }) | null
  if (!w || w.__dsIconTips) return
  w.__dsIconTips = true

  doc.addEventListener('pointerover', (e) => {
    if (e.pointerType && e.pointerType !== 'mouse') return
    const el = tipTarget(e.target)
    if (!el || el === shownFor) return
    window.clearTimeout(timer)
    if (shownFor) {
      // Moving from one icon button to the next: no second wait.
      showTip(el)
      return
    }
    timer = window.setTimeout(() => showTip(el), HOVER_DELAY)
  })
  doc.addEventListener('pointerout', (e) => {
    const from = tipTarget(e.target)
    if (!from) return
    const to = e.relatedTarget instanceof Node ? e.relatedTarget : null
    if (to && from.contains(to)) return
    hideTip()
  })
  doc.addEventListener('focusin', (e) => {
    const el = tipTarget(e.target)
    if (!el) return
    let keyboard = true
    try {
      keyboard = el.matches(':focus-visible')
    } catch {
      /* an old browser without :focus-visible */
    }
    if (keyboard) showTip(el)
  })
  doc.addEventListener('focusout', () => hideTip())
  doc.addEventListener('pointerdown', () => hideTip(), true)
  // Escape closes the tip and is still heard by whatever else is open.
  doc.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && shownFor) hideTip()
  })
  w.addEventListener('scroll', () => hideTip(), true)
  w.addEventListener('resize', () => hideTip())
}
